import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import Modal from "../../../components/shared/Modal";
import api from "../../../utils/api";

const EMPTY_CHAMBER = {
  location: "", address: "", startTime: "", endTime: "", fees: "", maxPatients: "",
};

const DAY_OPTIONS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export default function ChamberManagement() {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_CHAMBER);
  const [selectedDays, setSelectedDays] = useState([]);

  const { data, isLoading } = useQuery("doctor-chambers", () =>
    api.get("/doctor/chambers")
  );

  const saveMutation = useMutation(
    () =>
      editing
        ? api.put(`/doctor/chambers/${editing.id}`, {
            location: form.location,
            address: form.address,
            days: selectedDays.join(","),
            startTime: form.startTime,
            endTime: form.endTime,
            fees: form.fees,
            max_patients: form.maxPatients,
          })
        : api.post("/doctor/chambers", {
            location: form.location,
            address: form.address,
            days: selectedDays.join(","),
            startTime: form.startTime,
            endTime: form.endTime,
            fees: form.fees,
            max_patients: form.maxPatients,
          }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("doctor-chambers");
        toast.success(editing ? "Chamber updated" : "Chamber added");
        setShowModal(false);
        setEditing(null);
        setForm(EMPTY_CHAMBER);
        setSelectedDays([]);
      },
      onError: () => {
        toast.error("Something went wrong. Please try again.");
      },
    }
  );

  const deleteMutation = useMutation(
    (id) => api.delete(`/doctor/chambers/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("doctor-chambers");
        toast.success("Chamber deleted");
      },
      onError: () => {
        toast.error("Failed to delete chamber.");
      },
    }
  );

  const openEdit = (chamber) => {
    setEditing(chamber);
    setForm({
      location: chamber.location ?? "",
      address: chamber.address ?? "",
      startTime: chamber.start_time ? chamber.start_time.slice(0, 5) : "",
      endTime: chamber.end_time ? chamber.end_time.slice(0, 5) : "",
      fees: chamber.fees ?? "",
      maxPatients: chamber.max_patients ?? "",
    });
    setSelectedDays(chamber.days ? chamber.days.split(",") : []);
    setShowModal(true);
  };

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY_CHAMBER);
    setSelectedDays([]);
    setShowModal(true);
  };

  const toggleDay = (day) =>
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );

  const chambers = data?.chambers || [];

  return (
    <div className="space-y-5">
      <div className="flex justify-end">
        <button onClick={openAdd} className="btn-primary">
          + Add Chamber
        </button>
      </div>

      {isLoading ? (
        <div className="text-slate-500">Loading chambers…</div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {chambers.map((ch) => (
            <div key={ch.id} className="card">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-display font-semibold text-slate-800">
                    {ch.location}
                  </h3>
                  <p className="text-slate-500 text-sm mt-1">{ch.address}</p>
                  <div className="mt-2 space-y-1">
                    <p className="text-sm text-slate-600">
                      📅 {ch.days}
                    </p>
                    <p className="text-sm text-slate-600">
                      🕐 {ch.start_time?.slice(0, 5)} – {ch.end_time?.slice(0, 5)}
                    </p>
                    <p className="text-sm text-slate-600">
                      💰 ₹{ch.fees} | 👥 Max {ch.max_patients} patients
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => openEdit(ch)}
                    className="text-xs px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => deleteMutation.mutate(ch.id)}
                    disabled={deleteMutation.isLoading}
                    className="text-xs px-3 py-1 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
          {chambers.length === 0 && (
            <div className="col-span-2 text-center py-12 text-slate-400">
              No chambers added yet.
            </div>
          )}
        </div>
      )}

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editing ? "Edit Chamber" : "Add Chamber"}
        footer={
          <>
            <button onClick={() => setShowModal(false)} className="btn-secondary">
              Cancel
            </button>
            <button
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isLoading}
              className="btn-primary"
            >
              {saveMutation.isLoading ? "Saving…" : "Save"}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-500">
            A chamber is a location where the doctor sees patients on specific days.
          </p>

          {[
            { name: "location", label: "Location Name (e.g. City Clinic)" },
            { name: "address", label: "Full Address" },
            { name: "startTime", label: "Start Time", type: "time" },
            { name: "endTime", label: "End Time", type: "time" },
            { name: "fees", label: "Consultation Fees (₹)", type: "number" },
            { name: "maxPatients", label: "Max Patients Per Session", type: "number" },
          ].map(({ name, label, type = "text" }) => (
            <div key={name}>
              <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">
                {label}
              </label>
              <input
                type={type}
                value={form[name]}
                onChange={(e) => setForm((f) => ({ ...f, [name]: e.target.value }))}
                className="input-field"
              />
            </div>
          ))}

          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">
              Days Available
            </label>
            <div className="flex flex-wrap gap-2">
              {DAY_OPTIONS.map((day) => (
                <button
                  key={day}
                  type="button"
                  onClick={() => toggleDay(day)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                    selectedDays.includes(day)
                      ? "bg-primary-600 border-primary-600 text-white"
                      : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  {day.slice(0, 3)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}